import { EnvelopeSimple } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { getNetworkLogo } from '@/common/networkUtils';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import { InstanceState } from '@/common/util';
import StyledButton from '@/components/StyledButton';
import { useInstanceStatus } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import useMainStore from '@/store/mainStore';
import CheckoutSecondaryPriceDetails from './CheckoutSecondaryPriceDetails';
import CheckoutToken from './CheckoutToken';

export default function CheckoutSecondaryFlow({
  onConfirmCryptoCheckout,
}: {
  onConfirmCryptoCheckout: () => void;
}) {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const collectorEmail = useMainStore((state) => state.collectorEmail);
  const setCollectorEmail = useMainStore((state) => state.setCollectorEmail);
  const analytics = useAnalytics();
  const instanceStatus = useInstanceStatus();

  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');

  // Validate email when it changes
  useEffect(() => {
    if (collectorEmail) {
      // Basic email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = emailRegex.test(collectorEmail);

      if (!isValid) {
        setIsValidEmail(false);
        setEmailErrorMessage('Please enter a valid email address');
      } else {
        setIsValidEmail(true);
        setEmailErrorMessage('');
      }
    } else {
      // Always valid when empty (since it's optional)
      setIsValidEmail(true);
      setEmailErrorMessage('');
    }
  }, [collectorEmail]);

  const handleConfirmClick = () => {
    analytics.capture({
      event: TELEMETRY_EVENT_MAP.SECONDARY_CHECKOUT_BUTTON_CLICK,
    });
    onConfirmCryptoCheckout();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCollectorEmail(e.target.value);
  };

  if (!studioInstance) {
    return null;
  }

  const checkoutDisabled = !isValidEmail || instanceStatus === InstanceState.Live;

  return (
    <div className="m-claim--flex m-claim--size-full m-claim--flex-col">
      {/* Fixed Header */}
      <div className="m-claim--mb-4 m-claim--shrink-0 m-claim--pr-10 m-claim--text-xl m-claim--font-bold m-claim--text-primary">
        Secondary Market Checkout
      </div>

      {/* Scrollable Content */}
      <div className="m-claim--grow m-claim--overflow-y-auto m-claim--rounded-lg m-claim--bg-surface-secondary m-claim--px-3 m-claim--pb-3">
        {/* Receiving Section */}
        <div className="m-claim--mt-4 m-claim--flex m-claim--flex-col m-claim--gap-4">
          <div className="m-claim--flex m-claim--items-center">
            <div className="m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
              Purchasing
            </div>
            {studioInstance?.publicData.network && (
              <div className="m-claim--flex m-claim--items-center m-claim--gap-1 m-claim--rounded-full m-claim--bg-surface-secondary m-claim--px-2 m-claim--py-0.5">
                <div className="m-claim--flex m-claim--size-4 m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-secondary m-claim--p-0.5">
                  <img
                    src={getNetworkLogo(studioInstance.publicData.network)}
                    alt={`${StringUtils.capitalizeFirstLetter(Network.getDisplayNameForNetworkId(studioInstance.publicData.network))} network`}
                    className="m-claim--size-full m-claim--object-contain"
                  />
                </div>
                <span className="m-claim--text-xs m-claim--font-medium">
                  on{' '}
                  {StringUtils.capitalizeFirstLetter(
                    Network.getDisplayNameForNetworkId(studioInstance.publicData.network),
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Receiving Token */}
          <CheckoutToken
            image={studioInstance?.publicData.asset?.image ?? ''}
            name={studioInstance?.publicData.asset?.name ?? 'No name'}
            count={numberOfMints.toString()}
            // @dev you can only buy one at a time on secondary market
            disableQuantitySelector={true}
          />

          <hr />

          {/* Email Section */}
          <div className="m-claim--flex m-claim--flex-col m-claim--gap-2">
            <div className="m-claim--flex m-claim--items-center m-claim--gap-3">
              <div className="m-claim--flex m-claim--items-center m-claim--gap-2 m-claim--text-sm m-claim--font-semibold m-claim--text-secondary">
                <EnvelopeSimple
                  className="m-claim--size-4 m-claim--text-primary"
                  weight="fill"
                />
                <span className="m-claim--text-sm m-claim--font-semibold m-claim--text-primary">
                  Email
                </span>
              </div>
              <div className="m-claim--flex m-claim--items-center m-claim--justify-center m-claim--rounded-full m-claim--bg-surface-secondary m-claim--px-2 m-claim--py-0.5">
                <span className="m-claim--text-xs m-claim--font-medium m-claim--text-secondary">
                  Optional
                </span>
              </div>
            </div>

            <div className="m-claim--mb-2 m-claim--text-xs m-claim--text-secondary">
              Get updates from this creator about new drops and updates to existing drops.
            </div>

            <div className="m-claim--flex m-claim--items-center m-claim--gap-2">
              <div className="m-claim--relative m-claim--grow">
                <input
                  type="email"
                  value={collectorEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  className={`m-claim--w-full m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-3 m-claim--py-2 m-claim--text-sm focus:m-claim--outline-none focus:m-claim--ring-1 focus:m-claim--ring-surface-accent ${
                    !isValidEmail && collectorEmail ? 'm-claim--border-[#ef4444]' : ''
                  }`}
                  aria-label="Email address"
                />
                {!isValidEmail && collectorEmail && (
                  <div
                    className="m-claim--mt-1 m-claim--text-xs"
                    style={{ color: '#ef4444' }}
                  >
                    {emailErrorMessage}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="m-claim--mt-4 m-claim--shrink-0 m-claim--overflow-visible m-claim--border-default m-claim--pt-4">
        <CheckoutSecondaryPriceDetails />
        <div className="m-claim--py-4 m-claim--text-center">
          {checkoutDisabled && (
            <div className="m-claim--text-center m-claim--text-sm m-claim--font-semibold m-claim--text-warning">
              This mint is no longer available
            </div>
          )}
        </div>

        <div className="m-claim--flex m-claim--flex-row m-claim--gap-4">
          <StyledButton
            version="primary"
            onClick={handleConfirmClick}
            disabled={checkoutDisabled}
          >
            CONTINUE
          </StyledButton>
        </div>
      </div>
    </div>
  );
}
